import React, { useEffect, lazy, Suspense } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { motion, useAnimation } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { HeroSection } from '@/components/sections/HeroSection';
import { ProblemSection } from '@/components/sections/ProblemSection';
import { FeaturePillars } from '@/components/sections/FeaturePillars';
import { PersonaTabs } from '@/components/sections/PersonaTabs';
import { SocialProof } from '@/components/sections/SocialProof';

import { TrustLogos } from '@/components/sections/TrustLogos';
import { DemoContactForm } from '@/components/sections/DemoContactForm';
import { StickyTrialCTA } from '@/components/StickyTrialCTA';
import { CTASection } from '@/components/sections/CTASection';

const SimpleHowItWorks = lazy(() => import('@/components/sections/SimpleHowItWorks').then(m => ({ default: m.SimpleHowItWorks })));
const ROICalculator = lazy(() => import('@/components/sections/ROICalculator').then(m => ({ default: m.ROICalculator })));
const ExitIntentPopup = lazy(() => import('@/components/ExitIntentPopup').then(m => ({ default: m.ExitIntentPopup })));

const Index = () => {
  const controls = useAnimation();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isFrench = location.pathname.startsWith('/fr');
  
  if (isFrench && i18n.language !== 'fr') {
    i18n.changeLanguage('fr');
  } else if (!isFrench && i18n.language !== 'en') {
    i18n.changeLanguage('en');
  }
  
  useEffect(() => {
    controls.start({ opacity: 1, y: 0 });

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('scrollTo') === 'demo') {
      setTimeout(() => {
        const demoSection = document.getElementById('book-demo');
        if (demoSection) {
          demoSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, [controls, i18n, isFrench]);

  const scrollToDemo = () => {
    const demoSection = document.getElementById('book-demo');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const baseUrl = isFrench ? 'https://noq5.ai/fr' : 'https://noq5.ai/';
  const title = t('seo.home.title');
  const description = t('seo.home.description');
  
  return (
    <Layout>
      <SEOHead 
        title={title}
        description={description}
        keywords="NoQ5.ai, Revenue Intelligence Platform, AI CRM, HubSpot AI, evidence-based forecasting, zero-touch CRM, AI sales coach, pipeline visibility, deal intelligence, CRM automation, GDPR compliant AI, voice assistant for CRM, forecast accuracy, sales productivity" 
        canonicalUrl={baseUrl}
        hreflangLinks={[
          { lang: 'en', url: 'https://noq5.ai/' },
          { lang: 'fr', url: 'https://noq5.ai/fr' },
          { lang: 'x-default', url: 'https://noq5.ai/' }
        ]}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "NoQ5.ai",
          "applicationCategory": "BusinessApplication",
          "applicationSubCategory": "Revenue Intelligence Platform",
          "operatingSystem": "Web",
          "description": "AI-Powered Revenue Intelligence Platform. Zero-touch CRM updates, evidence-based forecasting, AI sales coaching, and deep HubSpot integration.",
          "url": "https://noq5.ai/",
          "screenshot": "https://noq5.ai/og-image-noq5.png",
          "softwareVersion": "2.0",
          "datePublished": "2024-01-01",
          "dateModified": "2026-02-11",
          "publisher": {
            "@type": "Organization",
            "name": "NoQ5.ai Technologies Ltd."
          },
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "priceValidUntil": "2026-12-31"
          },
          "featureList": ["Zero-touch CRM updates", "Evidence-based forecasting", "AI Sales Coach", "Command Center for leaders", "Deep HubSpot integration", "Google Workspace native", "GDPR compliance"]
        }, {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://noq5.ai/"
          }]
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [{
            "@type": "Question",
            "name": "What is NoQ5.ai?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "NoQ5.ai is an AI-Powered Revenue Intelligence Platform that autonomously keeps your CRM accurate, provides evidence-based forecasts, and coaches reps in real-time."
            }
          }, {
            "@type": "Question",
            "name": "How does NoQ5.ai handle privacy?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "NoQ5.ai is EU-based (Ireland) and GDPR-compliant. We anonymise sensitive data before prompting LLMs to protect customer information."
            }
          }, {
            "@type": "Question",
            "name": "What CRMs does NoQ5 integrate with?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "NoQ5 has deep integration with HubSpot with bi-directional sync, full field mapping, and automatic CRM updates."
            }
          }]
        }, {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "NoQ5.ai Technologies Ltd.",
          "description": "AI-Powered Revenue Intelligence Platform for modern sales teams.",
          "url": "https://noq5.ai",
          "logo": "https://noq5.ai/og-image-noq5.png",
          "foundingDate": "2025",
          "founders": [{
            "@type": "Person",
            "name": "Batiste Pasquet",
            "jobTitle": "CEO & Founder",
            "sameAs": "https://www.linkedin.com/in/batistepasquet/"
          }],
          "award": ["Web Summit 2025 High Potential Startup", "Next Wave Programme by Enterprise Ireland"]
        }]}
      />

      <div className="flex flex-col">
        {/* Hero — CRO/VP Sales focused */}
        <HeroSection onScrollToDemo={scrollToDemo} />

        {/* The Problem — pain by persona */}
        <ProblemSection />

        {/* Trust Logos */}
        <TrustLogos />

        {/* Feature Pillars — 6 value pillars */}
        <FeaturePillars />

        {/* Persona Tabs — self-select */}
        <PersonaTabs onScrollToDemo={scrollToDemo} />

        {/* How It Works */}
        <Suspense fallback={<div className="w-full py-12 flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}>
          <SimpleHowItWorks />
        </Suspense>

        {/* Social Proof */}
        <SocialProof />

        {/* ROI Calculator */}
        <Suspense fallback={<div className="w-full py-12 flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}>
          <ROICalculator />
        </Suspense>

        {/* Mid-page CTA */}
        <CTASection onScrollToDemo={scrollToDemo} />
        
        {/* FAQ Section */}
        <section className="w-full py-20 md:py-32 bg-muted/30">
          <div className="container px-4 md:px-6">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                {t('faq.title')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('faq.subtitle')}
              </p>
            </motion.div>
            
            <div className="mx-auto max-w-3xl space-y-4">
              {[
                { q: t('faq.questions.whatIs.question'), a: t('faq.questions.whatIs.answer') },
                { q: t('faq.questions.privacy.question'), a: t('faq.questions.privacy.answer') },
                { q: t('faq.questions.difference.question'), a: t('faq.questions.difference.answer') },
                { q: t('faq.questions.integrations.question'), a: t('faq.questions.integrations.answer') }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-card border border-border rounded-2xl p-6 shadow-subtle"
                >
                  <h3 className="text-lg font-semibold mb-3 text-foreground">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Final CTA — Book a Demo contact form */}
        <DemoContactForm />
      </div>

      {/* Sticky CTA Bar */}
      <StickyTrialCTA />

      <Suspense fallback={null}>
        <ExitIntentPopup />
      </Suspense>
    </Layout>
  );
};

export default Index;
